// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useRef, useEffect, useCallback, useState } from "react";
import { Eye, Code, X, Plus } from "lucide-react";
import { workflowStatusService } from "../../../services/workflowStatusService";
import LoadingOverlay from "../../LoadingOverlay";
import InlineNotification from "../../InlineNotification";
import useInlineNotification from "../../../hooks/useInlineNotification";

const EmailTemplateEditor = ({
  template,
  onSave,
  onCancel,
  onTemplateChange,
  selectedTemplateCode,
  workflowId,
  templates: externalTemplates,
  templatesLoading: externalTemplatesLoading,
}) => {
  const textareaRef = useRef(null);
  const [activeTab, setActiveTab] = useState("editor");
  const [templateContent, setTemplateContent] = useState("");
  const [previewData] = useState({
    customer_first_name: "Max",
    booking_number: "BK-2024-001",
    event_title: "Sommerfest 2024",
    event_date: "15. Juli 2024",
    event_time: "14:00 Uhr",
    venue: "Stadtplatz Event Center",
    total_amount: "1.250,00 €",
    organization_name: "Verein für Gemeinwohl",
    submission_date: "1. Juli 2024",
    next_review_tuesday: "9. Juli 2024",
  });

  const templates = externalTemplates || [];
  const templatesLoading = externalTemplatesLoading || false;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [__hasUserChanges, setHasUserChanges] = useState(false);

  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [newVariable, setNewVariable] = useState("");

  const [isSaving, setIsSaving] = useState(false);

  const { notification, showSuccess, showError, hideNotification } =
    useInlineNotification(5000);
  useEffect(() => {
    if (selectedTemplateCode && templates.length > 0) {
      let template = templates.find(
        (t) => t.template_code === selectedTemplateCode,
      );

      if (!template) {
        template = templates.find(
          (t) =>
            t.template_code?.toLowerCase() ===
            selectedTemplateCode?.toLowerCase(),
        );
      }

      if (template && template !== selectedTemplate) {
        setSelectedTemplate(template);
        setTemplateName(template.template_name || "");
        setTemplateSubject(template.subject || "");
        setIsActive(template.is_active || false);
        setTemplateContent(template.body_html || "");
        setHasUserChanges(false);
      }
    }
  }, [selectedTemplateCode, templates, selectedTemplate]);
  useEffect(() => {
    if (template) {
      setTemplateContent(template);
      setHasUserChanges(false);
    }
  }, [template]);

  const handleActiveChange = useCallback((e) => {
    const newActiveState = e.target.checked;
    setIsActive(newActiveState);
    setHasUserChanges(true);
  }, []);

  const handleTemplateNameChange = useCallback((e) => {
    setTemplateName(e.target.value);
    setHasUserChanges(true);
  }, []);

  const handleTemplateSubjectChange = useCallback((e) => {
    setTemplateSubject(e.target.value);
    setHasUserChanges(true);
  }, []);

  const handleTemplateChange = useCallback((e) => {
    const newValue = e.target.value;
    setTemplateContent(newValue);
    setHasUserChanges(true);
  }, []);

  const insertVariable = (variable) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateContent;
      const before = text.substring(0, start);
      const after = text.substring(end);

      const newText = before + variable + after;
      setTemplateContent(newText);
      setHasUserChanges(true);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + variable.length,
          start + variable.length,
        );
      }, 0);
    }
  };

  const addVariable = () => {
    if (newVariable.trim()) {
      const variableName = `{{${newVariable.trim()}}}`;
      if (!templateVariables.find((v) => v.name === variableName)) {
        console.error("Error adding new variable:", variableName);
      }
      setNewVariable("");
    }
  };

  const removeVariable = (variableName) => {
    console.error("Error removing variable:", variableName);
  };

  const generatePreview = useCallback(() => {
    let preview = templateContent || "";
    Object.entries(previewData).forEach(([key, value]) => {
      const variable = `{{${key}}}`;
      preview = preview.replace(new RegExp(variable, "g"), value);
    });
    return preview;
  }, [templateContent, previewData]);

  const handleSave = useCallback(async () => {
    if (!selectedTemplate?.template_id) {
      showError("Keine Template-ID gefunden. Speichern nicht möglich.");
      return;
    }

    setIsSaving(true);

    try {
      const templateData = {
        template_name: templateName,
        subject: templateSubject,
        is_active: isActive,
        body_html: templateContent,
        template_code: selectedTemplate.template_code,
        variables: selectedTemplate.variables || [],
        created_at: selectedTemplate.created_at,
        updated_at: new Date().toISOString(),
      };

      const updatedTemplate = await workflowStatusService.updateEmailTemplate(
        selectedTemplate.template_id,
        templateData,
      );

      let updatedNotificationsCount = 0;

      if (isActive !== selectedTemplate.is_active && workflowId) {
        try {
          const notificationsResponse =
            await workflowStatusService.getWorkflowEmailNotifications(
              workflowId,
            );

          const notifications = notificationsResponse.items || [];

          const templateNotifications = notifications.filter(
            (notification) =>
              notification.template_code === selectedTemplate.template_code,
          );

          if (templateNotifications.length > 0) {
            const updatePromises = templateNotifications.map((notification) => {
              const updateData = {
                ...notification,
                is_enabled: isActive,
                updated_at: new Date().toISOString(),
              };

              return workflowStatusService.updateWorkflowEmailNotification(
                notification.notification_id,
                updateData,
              );
            });

            const updatedNotifications = await Promise.all(updatePromises);
            updatedNotificationsCount = updatedNotifications.length;

            showSuccess(
              `Template und ${updatedNotifications.length} zugehörige E-Mail-Benachrichtigungen für diesen Workflow ${isActive ? "aktiviert" : "deaktiviert"}!`,
            );
          } else {
            showSuccess("Template erfolgreich gespeichert!");
          }
        } catch (notificationError) {
          console.error(
            "Error updating workflow email notifications:",
            notificationError,
          );
          showError(
            "Template gespeichert, aber Fehler beim Aktualisieren der E-Mail-Benachrichtigungen.",
          );
        }
      } else {
        showSuccess("Template erfolgreich gespeichert!");
      }

      if (onSave) {
        onSave({
          ...updatedTemplate,
          notificationsUpdated: updatedNotificationsCount,
          templateStatusChanged: isActive !== selectedTemplate.is_active,
        });
      }

      if (onTemplateChange) {
        onTemplateChange(templateContent);
      }

      onCancel();
    } catch (error) {
      console.error("Error saving template:", error);
      showError(
        "Fehler beim Speichern des Templates. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedTemplate,
    templateName,
    templateSubject,
    isActive,
    templateContent,
    workflowId,
    onSave,
    onCancel,
    onTemplateChange,
    showSuccess,
    showError,
  ]);

  const getTemplateVariables = useCallback(() => {
    if (selectedTemplate && selectedTemplate.variables) {
      return selectedTemplate.variables.map((varName) => ({
        name: `{{${varName}}}`,
        description: varName
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
      }));
    }

    return [
      { name: "{{customer_first_name}}", description: "Vorname des Kunden" },
      { name: "{{booking_number}}", description: "Buchungsnummer" },
      { name: "{{event_title}}", description: "Titel der Veranstaltung" },
      { name: "{{event_date}}", description: "Datum der Veranstaltung" },
      { name: "{{event_time}}", description: "Uhrzeit der Veranstaltung" },
      { name: "{{venue}}", description: "Veranstaltungsort" },
      { name: "{{total_amount}}", description: "Gesamtbetrag" },
      { name: "{{organization_name}}", description: "Name der Organisation" },
      { name: "{{submission_date}}", description: "Einreichungsdatum" },
      {
        name: "{{next_review_tuesday}}",
        description: "Nächster Prüfungstermin",
      },
    ];
  }, [selectedTemplate]);

  const templateVariables = getTemplateVariables();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center sm:p-4 p-0 z-50">
      <div className="bg-white sm:rounded-2xl shadow-2xl max-w-6xl w-full sm:max-h-[90vh] max-h-[100vh] h-full overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white sm:p-6 p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">E-Mail Template Editor</h2>
              <p className="text-blue-100 mt-1">
                Bearbeiten Sie die E-Mail-Vorlage
              </p>
            </div>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <InlineNotification
          notification={notification}
          onClose={hideNotification}
        />

        {templatesLoading ? (
          <LoadingOverlay loading={templatesLoading} />
        ) : (
          <div className="flex flex-col sm:flex-row sm:h-[calc(90vh-180px)] h-[calc(90vh-146px)] sm:overflow-none overflow-y-auto">
            <div
              className="w-full sm:w-1/2 sm:p-6 p-4 sm:overflow-y-auto overflow-none sm:border-r border-slate-200"
              style={{ scrollbarWidth: "thin" }}
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Grundinformationen
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Template Code
                      </label>
                      <input
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                        placeholder="template_code"
                        type="text"
                        value={selectedTemplate?.template_code || ""}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Template Name
                      </label>
                      <input
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        type="text"
                        value={templateName || ""}
                        onChange={handleTemplateNameChange}
                        placeholder="Template Name eingeben"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Betreff
                      </label>
                      <input
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="E-Mail Betreff mit {{variablen}}"
                        type="text"
                        value={templateSubject || ""}
                        onChange={handleTemplateSubjectChange}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2">
                        <input
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          type="checkbox"
                          checked={isActive || false}
                          onChange={handleActiveChange}
                        />
                        <span className="text-sm text-slate-700">
                          Template ist aktiv
                        </span>
                      </label>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 ${isActive ? "text-green-600" : "text-red-600"}`}
                        >
                          {isActive ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                width="20"
                                height="12"
                                x="2"
                                y="6"
                                rx="6"
                                ry="6"
                              ></rect>
                              <circle cx="16" cy="12" r="2"></circle>
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                width="20"
                                height="12"
                                x="2"
                                y="6"
                                rx="6"
                                ry="6"
                              ></rect>
                              <circle cx="6" cy="12" r="2"></circle>
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Template Variablen
                  </h3>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="neue_variable"
                        type="text"
                        value={newVariable}
                        onChange={(e) => setNewVariable(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addVariable()}
                      />
                      <button
                        onClick={addVariable}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {templateVariables.map((variable, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-200 transition-colors"
                          onClick={() => insertVariable(variable.name)}
                          title={`Klicken um "${variable.name}" einzufügen`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3 h-3"
                          >
                            <line x1="4" x2="20" y1="9" y2="9"></line>
                            <line x1="4" x2="20" y1="15" y2="15"></line>
                            <line x1="10" x2="8" y1="3" y2="21"></line>
                            <line x1="16" x2="14" y1="3" y2="21"></line>
                          </svg>
                          {variable.name}
                          <button
                            className="ml-1 hover:text-blue-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeVariable(variable.name);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-3 h-3"
                            >
                              <path d="M18 6 6 18"></path>
                              <path d="m6 6 12 12"></path>
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col">
              <div className="border-b border-slate-200 sm:px-6 px-4 sm:pt-6 pt-1">
                <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg">
                  <button
                    onClick={() => setActiveTab("editor")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "editor"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    HTML
                  </button>
                  <button
                    onClick={() => setActiveTab("preview")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "preview"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Vorschau
                  </button>
                </div>
              </div>
              <div
                className="flex-1 sm:p-6 p-4 sm:overflow-y-auto overflow-none"
                style={{ scrollbarWidth: "thin" }}
              >
                {activeTab === "editor" ? (
                  <textarea
                    ref={textareaRef}
                    className="w-full h-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 resize-none min-h-[500px] sm:min-h-0"
                    style={{ scrollbarWidth: "thin" }}
                    placeholder="HTML Template..."
                    value={templateContent}
                    onChange={handleTemplateChange}
                  />
                ) : (
                  <div className="w-full h-full px-4 py-3 border border-slate-200 rounded-lg text-sm overflow-y-auto">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: generatePreview() }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="bg-slate-50 border-t border-slate-200 sm:px-6 px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0">
            <div className="text-sm text-slate-600">
              ID: {selectedTemplate?.template_id || "et-1"} | Zuletzt
              aktualisiert: {new Date().toLocaleDateString("de-DE")},{" "}
              {new Date().toLocaleTimeString("de-DE")}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-rose-700 border border-rose-300 bg-rose-100 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  isSaving
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isSaving ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Speichern...
                  </div>
                ) : (
                  "Speichern"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EmailTemplateEditor;
